import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Phone, Mail, MapPin, Globe, Shield, FileText, Cookie } from "lucide-react";
import { EMAIL, PHONE, PHONE_TEL, LOGO_URL, FACEBOOK_URL } from "../lib/utils";

export default function MentionsLegales() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Mentions légales — Paintball Aventures 35";
  }, []);

  return (
    <div className="bg-white text-black font-body">
      {/* Header */}
      <header className="border-b-2 border-black sticky top-0 z-40 bg-white/95 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 h-20 flex items-center justify-between gap-4">
          <Link
            to="/"
            data-testid="mentions-back-home"
            className="flex items-center gap-3 group"
          >
            <img
              src={LOGO_URL}
              alt="Logo Paintball Aventures 35"
              className="h-12 w-12 object-contain"
            />
            <div className="leading-tight">
              <p className="font-display text-base md:text-lg font-bold uppercase tracking-tight">
                Paintball Aventures 35
              </p>
              <p className="font-mono text-[10px] text-[#E91E63] font-bold tracking-[0.2em]">
                Mentions légales
              </p>
            </div>
          </Link>
          <Link
            to="/"
            data-testid="mentions-back-button"
            className="inline-flex items-center gap-2 px-3 md:px-5 py-2.5 border-2 border-black text-black font-bold uppercase text-xs md:text-sm tracking-wider hover:bg-black hover:text-white transition-colors whitespace-nowrap"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={2.5} />
            <span className="hidden sm:inline">Retour à l&apos;accueil</span>
            <span className="sm:hidden">Retour</span>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-white py-16 md:py-20 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10">
          <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-[#E91E63] mb-3">
            Informations légales
          </p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold uppercase leading-[0.95] tracking-tight">
            Mentions <span className="text-[#E91E63]">légales</span>
          </h1>
          <p className="mt-4 text-base md:text-lg text-gray-700 max-w-2xl">
            Conformément à l&apos;article 6-III-1 de la loi pour la Confiance dans l&apos;Économie Numérique du 21 juin 2004 (LCEN), nous portons à la connaissance des utilisateurs les informations suivantes.
          </p>
        </div>
      </section>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-12 md:py-16 space-y-12">
        {/* Editor */}
        <Section icon={Shield} number="01" title="Éditeur du site">
          <Grid>
            <Row label="Dénomination sociale">N ET T TUMOINE</Row>
            <Row label="Nom commercial">Paintball Aventures 35</Row>
            <Row label="Forme juridique">SARL (Société à Responsabilité Limitée) — EURL</Row>
            <Row label="Capital social">4 000,00 €</Row>
            <Row label="Siège social">35420 Louvigné-du-Désert, France</Row>
            <Row label="Sites d'exploitation">
              <span className="block">20 Boulevard Nelson Mandela, 35300 Fougères</span>
              <span className="block">La Morinais, 35420 Louvigné-du-Désert</span>
            </Row>
            <Row label="SIREN">818 982 225</Row>
            <Row label="SIRET (siège)">818 982 225 00018</Row>
            <Row label="RCS">818 982 225 R.C.S. Rennes</Row>
            <Row label="Inscription au RCS">18/03/2016</Row>
            <Row label="N° TVA intracommunautaire">FR28818982225</Row>
            <Row label="Directeur de la publication">Thomas TUMOINE, gérant</Row>
            <Row label="Téléphone">
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="mentions-phone-link"
                className="text-[#E91E63] hover:underline font-semibold"
              >
                {PHONE}
              </a>
            </Row>
            <Row label="Email">
              <a
                href={`mailto:${EMAIL}`}
                data-testid="mentions-email-link"
                className="text-[#E91E63] hover:underline break-all"
              >
                {EMAIL}
              </a>
            </Row>
          </Grid>
        </Section>

        {/* Hosting */}
        <Section icon={Globe} number="02" title="Hébergement du site">
          <p className="text-base text-gray-800 leading-relaxed">
            Le présent site est hébergé par :
          </p>
          <Grid>
            <Row label="Hébergeur">Wix.com, Inc.</Row>
            <Row label="Adresse">500 Terry A. Francois Boulevard, 6th Floor, San Francisco, CA 94158, États-Unis</Row>
            <Row label="Siège européen">Wix.com Ltd. — 40 Namal Tel Aviv St., Tel Aviv 6350671, Israël</Row>
            <Row label="Site web">
              <a
                href="https://www.wix.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#E91E63] hover:underline"
              >
                www.wix.com
              </a>
            </Row>
          </Grid>
        </Section>

        {/* IP */}
        <Section icon={FileText} number="03" title="Propriété intellectuelle">
          <p className="text-base text-gray-800 leading-relaxed">
            L&apos;ensemble du contenu présent sur le site <strong>paintball-aventures.com</strong> (textes, photographies, vidéos, logos, identité visuelle, charte graphique, illustrations, icônes) est la propriété exclusive de la société <strong>N ET T TUMOINE</strong> (Paintball Aventures 35), à l&apos;exception des contenus tiers (images d&apos;illustration, fonds de carte Google Maps, polices Google Fonts &amp; Fontshare, icônes Lucide).
          </p>
          <p className="text-base text-gray-800 leading-relaxed mt-3">
            Toute reproduction, représentation, modification, publication ou adaptation totale ou partielle, par quelque procédé que ce soit, est strictement interdite sans l&apos;autorisation écrite préalable de l&apos;éditeur, conformément aux articles L.122-4 et L.122-5 du Code de la propriété intellectuelle. Toute utilisation non autorisée constitue une contrefaçon sanctionnée par les articles L.335-2 et suivants du même code.
          </p>
        </Section>

        {/* Liability */}
        <Section icon={Shield} number="04" title="Limitation de responsabilité">
          <p className="text-base text-gray-800 leading-relaxed">
            Les informations diffusées sur ce site (tarifs, horaires, descriptions des terrains, formules, conditions de réservation) sont communiquées à titre indicatif et peuvent évoluer sans préavis. <strong>L&apos;activité paintball est accessible uniquement sur réservation préalable</strong> ; aucune session ne peut être confirmée sans validation de l&apos;équipe.
          </p>
          <p className="text-base text-gray-800 leading-relaxed mt-3">
            L&apos;éditeur ne saurait être tenu responsable des dommages directs ou indirects résultant de l&apos;accès, de l&apos;utilisation du site, d&apos;une éventuelle interruption de service, ou de l&apos;exploitation des informations diffusées. L&apos;utilisateur reconnaît utiliser ces informations sous sa seule et entière responsabilité.
          </p>
          <p className="text-base text-gray-800 leading-relaxed mt-3">
            La pratique du paintball implique le respect strict des consignes de sécurité données sur place par l&apos;équipe d&apos;animation (port du masque obligatoire en zone de jeu, etc.). Pour les mineurs, l&apos;<strong>autorisation parentale</strong> téléchargeable sur le site est obligatoire.
          </p>
        </Section>

        {/* GDPR */}
        <Section icon={Shield} number="05" title="Données personnelles &amp; RGPD">
          <p className="text-base text-gray-800 leading-relaxed">
            Conformément au Règlement Général sur la Protection des Données (RGPD — UE 2016/679) et à la loi « Informatique et Libertés » du 6 janvier 1978 modifiée, l&apos;utilisateur dispose d&apos;un droit d&apos;accès, de rectification, d&apos;effacement, de limitation, de portabilité et d&apos;opposition au traitement des données le concernant.
          </p>

          <div className="mt-5 border-2 border-black bg-[#fafafa] p-5 md:p-6">
            <p className="font-display text-xl font-bold uppercase mb-3 leading-tight">
              Données collectées via le formulaire de pré-réservation
            </p>
            <ul className="list-disc pl-5 space-y-2 text-sm text-gray-800">
              <li><strong>Données collectées</strong> : nom, prénom, téléphone, email, date souhaitée, nombre de participants, forfait, site, option restauration, informations libres.</li>
              <li><strong>Finalité</strong> : traitement de votre demande de pré-réservation et prise de contact pour confirmation.</li>
              <li><strong>Base légale</strong> : exécution de mesures précontractuelles à la demande de l&apos;utilisateur (art. 6.1.b RGPD).</li>
              <li><strong>Modalité technique</strong> : le formulaire ouvre votre logiciel de messagerie avec un email pré-rempli ; <em>aucune donnée n&apos;est stockée sur le site ni sur un serveur tiers</em>. La transmission se fait directement par votre client mail.</li>
              <li><strong>Destinataire</strong> : Thomas TUMOINE, gérant — tumoinethomas@hotmail.fr.</li>
              <li><strong>Durée de conservation</strong> : conservation des emails reçus pendant 3 ans à compter du dernier contact, à des fins de gestion clientèle.</li>
            </ul>
          </div>

          <p className="text-base text-gray-800 leading-relaxed mt-5">
            Pour exercer vos droits ou pour toute question relative à vos données personnelles, vous pouvez nous contacter :
          </p>
          <ul className="mt-2 space-y-1.5 text-sm text-gray-800">
            <li className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#E91E63]" strokeWidth={2.5} />
              <a href={`mailto:${EMAIL}`} className="text-[#E91E63] hover:underline">
                {EMAIL}
              </a>
            </li>
            <li className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-[#E91E63]" strokeWidth={2.5} />
              <a href={`tel:${PHONE_TEL}`} className="text-[#E91E63] hover:underline">
                {PHONE}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 text-[#E91E63]" strokeWidth={2.5} />
              <span>N ET T TUMOINE — 35420 Louvigné-du-Désert</span>
            </li>
          </ul>
          <p className="text-sm text-gray-600 leading-relaxed mt-5">
            Vous disposez également du droit d&apos;introduire une réclamation auprès de la <strong>CNIL</strong> (Commission Nationale de l&apos;Informatique et des Libertés) — 3 place de Fontenoy, TSA 80715, 75334 Paris Cedex 07 — <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-[#E91E63] hover:underline">www.cnil.fr</a>.
          </p>
        </Section>

        {/* Cookies */}
        <Section icon={Cookie} number="06" title="Cookies &amp; traceurs">
          <p className="text-base text-gray-800 leading-relaxed">
            Le présent site <strong>n&apos;utilise aucun cookie de mesure d&apos;audience, de publicité ou de profilage</strong>. Seuls des cookies strictement nécessaires au fonctionnement de la navigation peuvent être déposés (cookies techniques exemptés de consentement au sens de l&apos;article 82 de la loi Informatique et Libertés).
          </p>
          <p className="text-base text-gray-800 leading-relaxed mt-3">
            Les pages incluant un <strong>plan Google Maps</strong> peuvent déposer des cookies par Google : pour plus d&apos;informations, consultez la <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#E91E63] hover:underline">politique de confidentialité Google</a>.
          </p>
        </Section>

        {/* External links */}
        <Section icon={Globe} number="07" title="Liens externes">
          <p className="text-base text-gray-800 leading-relaxed">
            Le site peut contenir des liens vers d&apos;autres sites (Facebook, Instagram, TikTok, Google Maps, Google Search, plateforme de pré-réservation). L&apos;éditeur n&apos;exerce aucun contrôle sur ces ressources externes et décline toute responsabilité quant à leur contenu, leur disponibilité ou les pratiques de traitement de données qui leur sont propres.
          </p>
        </Section>

        {/* Applicable law */}
        <Section icon={FileText} number="08" title="Droit applicable">
          <p className="text-base text-gray-800 leading-relaxed">
            Les présentes mentions légales sont régies par le <strong>droit français</strong>. En cas de litige, et après tentative de recherche d&apos;une solution amiable, compétence exclusive est attribuée aux tribunaux de <strong>Rennes</strong>, lieu du siège social.
          </p>
        </Section>

        {/* Update date */}
        <div className="pt-8 border-t-2 border-black text-sm text-gray-500 font-mono uppercase tracking-wider flex flex-col md:flex-row md:items-center justify-between gap-2">
          <span>Dernière mise à jour : décembre 2025</span>
          <a
            href={FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-[#E91E63] transition-colors"
          >
            Suivez-nous sur Facebook ↗
          </a>
        </div>
      </main>

      {/* Mini footer */}
      <footer className="bg-black text-white py-10 border-t-2 border-black">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <p className="text-sm text-white/70">
            © {new Date().getFullYear()} Paintball Aventures 35 — Paintball Fougères &amp; Louvigné-du-Désert
          </p>
          <Link
            to="/"
            data-testid="mentions-footer-home"
            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#E91E63] hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={2.5} />
            Retour à l&apos;accueil
          </Link>
        </div>
      </footer>
    </div>
  );
}

function Section({ icon: Icon, number, title, children }) {
  return (
    <section>
      <div className="flex items-center gap-3 mb-5">
        <span className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-[#E91E63]">
          {number}
        </span>
        <span className="w-12 h-px bg-[#E91E63]" />
        <Icon className="w-4 h-4 text-[#E91E63]" strokeWidth={2.5} />
      </div>
      <h2 className="font-display text-2xl md:text-3xl font-bold uppercase leading-tight tracking-tight mb-5">
        {title}
      </h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function Grid({ children }) {
  return (
    <dl className="grid sm:grid-cols-[180px_1fr] gap-x-6 gap-y-2 border-2 border-black p-5 md:p-6 bg-[#fafafa]">
      {children}
    </dl>
  );
}

function Row({ label, children }) {
  return (
    <>
      <dt className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 self-center">
        {label}
      </dt>
      <dd className="text-sm md:text-base text-gray-900 font-semibold pb-2 sm:pb-0 border-b sm:border-b-0 border-gray-200">
        {children}
      </dd>
    </>
  );
}
